import { Query } from 'appwrite';
import { appwriteConfig, databaseId, databases } from '../config/appwrite';
import { KIAuthorAutomaticRun, KIAuthorCharacter, KIAuthorFeed, KIAuthorWebhookResult, KIAuthorsConfig } from '../types';
const CONFIG_DOC_ID = 'global';

const DEFAULT_WEBHOOK_URL = 'https://n8n.koll.cc/webhook/ab747d65-b02f-43f0-afce-3709f4f1d764';

const DEFAULT_FEEDS: KIAuthorFeed[] = [
  { url: 'https://www.tagesschau.de/index~rss2.xml', outlet: 'Tagesschau' },
  { url: 'https://feeds.cms.wiwo.de/rss/schlagzeilen', outlet: 'WirtschaftsWoche' },
  { url: 'https://nius.de/rss', outlet: 'NIUS' },
  { url: 'https://www.welt.de/feeds/topnews.rss', outlet: 'WELT' },
  { url: 'https://www.agrarheute.com/news.xml', outlet: 'Agrarheute' }
];

const DEFAULT_CHARACTERS: KIAuthorCharacter[] = [
  {
    name: 'analytiker',
    character: 'Nuechterner Analytiker',
    time: '08:00',
    enabled: true,
    stylePrompt:
      'Dein Stil:\n- nuechtern\n- klar\n- praezise\n- sachlich\n- verstaendlich\n- keine Albernheit\n- keine Uebertreibung\n- keine Floskeln\n- arbeite staatlichen Eingriff, Fehlanreize oder Freiheitsverlust sauber heraus.'
  },
  {
    name: 'spoetter',
    character: 'Satirischer Spoetter',
    time: '10:00',
    enabled: true,
    stylePrompt:
      'Dein Stil:\n- bissig\n- ironisch\n- pointiert\n- unterhaltsam\n- mit leichter Ueberzeichnung\n- mit Spott, aber inhaltlich nachvollziehbar\n- Absatz 1 bleibt trotzdem sachlich und neutral\n- die satirische Zuspitzung beginnt vor allem in Absatz 2.'
  },
  {
    name: 'eigentumsdenker',
    character: 'Juristisch-praeziser Eigentumsdenker',
    time: '12:00',
    enabled: true,
    stylePrompt:
      'Dein Stil:\n- klar\n- argumentativ\n- strukturiert\n- juristisch praezise\n- ohne Pathos\n- betone Eigentum, Vertragsfreiheit, Verantwortung, Haftung und Grenzen staatlicher Gewalt.'
  },
  {
    name: 'kulturkritiker',
    character: 'Historisch gebildeter Kulturkritiker',
    time: '14:00',
    enabled: true,
    stylePrompt:
      'Dein Stil:\n- gebildet, aber gut lesbar\n- kulturkritisch\n- historisch einordnend\n- ordne den Vorgang in groessere Entwicklungen ein, etwa Staatsausbau, Zentralisierung, Entmuendigung oder Zivilisationsmuedigkeit.'
  },
  {
    name: 'marktbeobachter',
    character: 'Trocken-zynischer Marktbeobachter',
    time: '16:00',
    enabled: true,
    stylePrompt:
      'Dein Stil:\n- trocken\n- lakonisch\n- leicht zynisch\n- oekonomisch orientiert\n- zeige Fehlanreize, Buerokratiekosten, Umverteilung, regulatorische Nebenwirkungen und Marktverzerrungen auf\n- kurze trockene Spitzen sind erwuenscht.'
  }
];

const DEFAULT_SYSTEM_PROMPT =
  'Du bist ein libertaerer Kolumnist.\n\nAllgemeine Regeln:\n- Schreibe einen fertigen kurzen Meinungsartikel fuer Leser.\n- Keine Ueberschrift.\n- Kein Markdown.\n- Keine Bulletpoints.\n- Keine Meta-Saetze ueber den Schreibprozess.\n- Keine Einleitung wie "Hier ist ein Kommentar".\n- Absatz 1 ist eine knappe, sachliche Einordnung des Vorgangs.\n- Absatz 1 erklaert dem Leser auf Basis des gelieferten Artikels, worum es konkret geht, wer was plant, fordert, getan hat oder worueber gestritten wird.\n- Absatz 1 bleibt neutral im Ton und verdichtet nur Informationen aus dem gelieferten Artikel.\n- Absatz 2 ist der eigentliche libertaere Kommentar.\n- Absatz 2 soll klar Position beziehen und organisch aus Absatz 1 folgen.\n- Erfinde keine zusaetzlichen Fakten.\n- Bleibe inhaltlich eng am gelieferten Artikel.\n- Starte moeglichst konkret mit dem beschriebenen Vorgang, nicht mit allgemeinen Floskeln.\n- Laenge insgesamt: 350 bis 500 Woerter.\n- Schreibe implizit libertaer und verwende keine Floskeln wie: "Aus libertaerer Sicht".\n- Schreibe auf Deutsch.';

const getDefaultConfig = (): KIAuthorsConfig => ({
  autoPublishEnabled: false,
  autoPublishTime: '08:00',
  webhookUrl: DEFAULT_WEBHOOK_URL,
  commonSystemPrompt: DEFAULT_SYSTEM_PROMPT,
  feeds: DEFAULT_FEEDS,
  characters: DEFAULT_CHARACTERS
});

const HHMM_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

const normalizeCharacter = (character: Partial<KIAuthorCharacter>, fallback: KIAuthorCharacter): KIAuthorCharacter => ({
  name: character.name || '',
  character: character.character || '',
  stylePrompt: character.stylePrompt || '',
  time: typeof character.time === 'string' && HHMM_REGEX.test(character.time) ? character.time : fallback.time,
  // Bestehende Configs ohne enabled-Feld bleiben standardmäßig aktiv.
  enabled: typeof character.enabled === 'boolean' ? character.enabled : true
});

export class KIAuthorsService {
  static async getConfig(): Promise<KIAuthorsConfig> {
    try {
      const doc = await databases.getDocument(
        databaseId,
        appwriteConfig.collections.kiAuthorsConfigs,
        CONFIG_DOC_ID
      );

      const payload: KIAuthorsConfig = {
        autoPublishEnabled: doc.autoPublishEnabled === true,
        autoPublishTime: doc.autoPublishTime || '08:00',
        webhookUrl: doc.webhookUrl || DEFAULT_WEBHOOK_URL,
        commonSystemPrompt: doc.commonSystemPrompt || DEFAULT_SYSTEM_PROMPT,
        feeds: doc.feeds ? JSON.parse(doc.feeds) : DEFAULT_FEEDS,
        characters: doc.characters ? JSON.parse(doc.characters) : DEFAULT_CHARACTERS
      };

      return {
        autoPublishEnabled: payload.autoPublishEnabled,
        autoPublishTime: payload.autoPublishTime,
        webhookUrl: payload.webhookUrl || DEFAULT_WEBHOOK_URL,
        commonSystemPrompt: payload.commonSystemPrompt || DEFAULT_SYSTEM_PROMPT,
        feeds: Array.isArray(payload.feeds) ? payload.feeds : DEFAULT_FEEDS,
        characters:
          Array.isArray(payload.characters) && payload.characters.length === 5
            ? payload.characters.map((character, index) => normalizeCharacter(character, DEFAULT_CHARACTERS[index]))
            : DEFAULT_CHARACTERS
      };
    } catch (error: any) {
      if (error?.code === 404) return getDefaultConfig();
      throw error;
    }
  }

  static async saveConfig(config: KIAuthorsConfig, userId: string): Promise<void> {
    try {
      const data = {
        autoPublishEnabled: config.autoPublishEnabled,
        autoPublishTime: config.autoPublishTime,
        webhookUrl: config.webhookUrl,
        commonSystemPrompt: config.commonSystemPrompt,
        feeds: JSON.stringify(config.feeds),
        characters: JSON.stringify(config.characters),
        updatedBy: userId
      };

      try {
        await databases.updateDocument(
          databaseId,
          appwriteConfig.collections.kiAuthorsConfigs,
          CONFIG_DOC_ID,
          data
        );
        return;
      } catch (updateError: any) {
        // Wenn das Dokument noch nicht existiert, wird es erstellt.
        if (updateError?.code !== 404) throw updateError;
      }

      await databases.createDocument(
        databaseId,
        appwriteConfig.collections.kiAuthorsConfigs,
        CONFIG_DOC_ID,
        data
      );
    } catch (error: any) {
      const message = error?.response?.message || error?.message || 'Speichern fehlgeschlagen. Bitte versuche es erneut.';
      throw new Error(message);
    }
  }

  static async getLatestAutomaticRun(): Promise<KIAuthorAutomaticRun | null> {
    const result = await databases.listDocuments(databaseId, appwriteConfig.collections.kiAuthorsRuns,
      [Query.orderDesc('$createdAt'), Query.limit(1)]);
    return (result.documents[0] as unknown as KIAuthorAutomaticRun) || null;
  }

  static async requestArticles(config: KIAuthorsConfig): Promise<KIAuthorWebhookResult[]> {
    const selectedCharacters = config.characters.filter(character => character.enabled);
    if (selectedCharacters.length === 0) {
      throw new Error('Bitte mindestens einen KI-Autor auswaehlen.');
    }

    const authorNames = selectedCharacters.map(character => character.name.trim().toLowerCase());
    const authorLabels = selectedCharacters.map(character => character.character.trim().toLowerCase());
    if (authorNames.some(name => !name) || authorLabels.some(label => !label)
      || new Set(authorNames).size !== selectedCharacters.length
      || new Set(authorLabels).size !== selectedCharacters.length) {
      throw new Error('Ausgewaehlte KI-Autoren brauchen eindeutige Namen und Charakterbezeichnungen.');
    }

    // Feeds liefern die Themenauswahl. Die Artikelzahl richtet sich ausschließlich
    // nach den ausgewählten Autoren und wird zusätzlich im n8n-Workflow geprüft.
    if (config.feeds.length === 0) {
      throw new Error('Bitte mindestens einen RSS-Feed hinterlegen.');
    }
    const response = await fetch(config.webhookUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        feeds: config.feeds,
        commonSystemPrompt: config.commonSystemPrompt,
        characters: selectedCharacters,
        articleCount: selectedCharacters.length
      })
    });

    if (!response.ok) {
      throw new Error(`Webhook-Fehler (${response.status})`);
    }

    const data = await response.json();
    if (!Array.isArray(data)) {
      throw new Error('Webhook hat kein gueltiges Array zurueckgegeben');
    }

    const expectedAuthors = new Set(selectedCharacters.map(character => character.character));
    const returnedAuthors = data.map(item => item?.author);
    if (data.length !== selectedCharacters.length
      || new Set(returnedAuthors).size !== selectedCharacters.length
      || returnedAuthors.some(author => !expectedAuthors.has(author))) {
      throw new Error('Webhook hat nicht genau einen Artikel pro ausgewaehltem KI-Autor zurueckgegeben. Bitte den n8n-Workflow pruefen.');
    }

    return data as KIAuthorWebhookResult[];
  }
}
